// Sync controller for the main slide (WSI) viewer and the SectionStar block-image
// viewer. See phase1-prototype-plan.md and architecture-syncview.md §6.3.
//
// One module-level instance owns all per-frame work, so nothing here goes through
// React state or Redux on every frame. Redux only holds the user-level state (synced
// or not, the transform, landmarks); viewer-container.js mirrors it into this module.
//
// Two modes while both viewers are registered:
//   aligning - zoom is linked by physical scale (same µm per screen pixel); pan is not.
//   synced   - zoom and centre both follow through the transform T, rotation is locked,
//              and a red box shows the WSI field of view once the block image is at its
//              zoom limit.
//
// Following is driven by value, not by events from a particular pane: every
// viewport-change schedules one animation-frame tick, which compares each pane with
// the state it last saw or wrote. Whichever pane changed is the leader. That way the
// WSI zoom buttons (which live in the toolbar, outside the viewer element) and the
// keyboard work without any special casing, and our own writes to the follower never
// echo back as a change.

import OpenSeadragon from "openseadragon";
import {
  applyInverse,
  applyTransform,
  fitLandmarksWithScaleCheck,
  followerRotation,
  formatFov,
  fovBoxSize,
  fovMicrons,
  matchImageZoom,
} from "./viewer-sync-math";

const ZOOM_EPSILON = 1e-6; // relative
const CENTER_EPSILON = 1e-9; // viewport units (image width = 1)
const AT_LIMIT = 1e-3; // relative tolerance for "at the zoom limit"
const ROTATE_FLIP_KEYS = new Set([82, 70]); // OSD 4.1.1 defaults: r/R rotate, f/F flip

const state = {
  wsi: null, // { viewer, mpp, assumed, cleanup }
  ss: null, // { viewer, mpp, cleanup, box, label }
  synced: false,
  T: null,
  // Virtual SectionStar image zoom: what it would need to match the WSI, even when
  // that is past what the block image can show.
  wantedIz: null,
  last: { wsi: null, ss: null },
  lastInput: null, // "wsi" | "ss", most recent pointer/wheel input
  raf: 0,
  pointHandler: null,
  overlays: [],
  landmarksVisible: false,
  // Redux bridge (setSyncHandlers) and the last block image's identity, for
  // carrying an alignment over a White <-> ClaraSight switch.
  getSync: null,
  onSyncLost: null,
  lastSsInfo: null,
  savedWsiConstraints: null,
};

/* ---- viewport helpers (all in image pixels of the pane's own image) -------------- */

const tiled = (pane) => pane && pane.viewer.world && pane.viewer.world.getItemAt(0);
const ready = (pane) => !!(pane && pane.viewer.viewport && tiled(pane));

const imageZoom = (pane) =>
  tiled(pane).viewportToImageZoom(pane.viewer.viewport.getZoom(true));

const setImageZoom = (pane, iz, immediately) => {
  pane.viewer.viewport.zoomTo(tiled(pane).imageToViewportZoom(iz), null, immediately);
};

const maxImageZoom = (pane) =>
  tiled(pane).viewportToImageZoom(pane.viewer.viewport.getMaxZoom());

const centerInImage = (pane) =>
  tiled(pane).viewportToImageCoordinates(pane.viewer.viewport.getCenter(true));

const panToImage = (pane, p, immediately) => {
  pane.viewer.viewport.panTo(tiled(pane).imageToViewportCoordinates(p.x, p.y), immediately);
};

const snapshot = (pane) => {
  const c = pane.viewer.viewport.getCenter(true);
  return { x: c.x, y: c.y, zoom: pane.viewer.viewport.getZoom(true) };
};

const changed = (now, before) =>
  !before ||
  Math.abs(now.zoom - before.zoom) > ZOOM_EPSILON * before.zoom ||
  Math.abs(now.x - before.x) > CENTER_EPSILON ||
  Math.abs(now.y - before.y) > CENTER_EPSILON;

const remember = () => {
  state.last.wsi = ready(state.wsi) ? snapshot(state.wsi) : null;
  state.last.ss = ready(state.ss) ? snapshot(state.ss) : null;
};

/* ---- following ------------------------------------------------------------------- */

// The WSI moved: set the block image's zoom (capped at its limit) and, when synced,
// its centre.
const followFromWsi = () => {
  const { wsi, ss } = state;
  const wanted = matchImageZoom(imageZoom(wsi), wsi.mpp, ss.mpp);
  state.wantedIz = wanted;
  setImageZoom(ss, Math.min(wanted, maxImageZoom(ss)), true);
  if (state.synced && state.T) {
    panToImage(ss, applyTransform(state.T, centerInImage(wsi)), true);
  }
};

// The block image moved: set the WSI's zoom from the block image's (or from the
// virtual zoom while it is held at its limit) and, when synced, its centre.
const followFromSs = () => {
  const { wsi, ss } = state;
  const iz = imageZoom(ss);
  const max = maxImageZoom(ss);
  if (iz < max * (1 - AT_LIMIT) || state.wantedIz === null) {
    state.wantedIz = iz;
  } else {
    // At the limit: a pan must not pull the WSI back out to the limit's scale.
    state.wantedIz = Math.max(state.wantedIz, iz);
  }
  const target = matchImageZoom(state.wantedIz, ss.mpp, wsi.mpp);
  if (Math.abs(imageZoom(wsi) - target) > ZOOM_EPSILON * target) {
    setImageZoom(wsi, target, true);
  }
  if (state.synced && state.T) {
    panToImage(wsi, applyInverse(state.T, centerInImage(ss)), true);
  }
};

const tick = () => {
  state.raf = 0;
  if (!ready(state.wsi) || !ready(state.ss)) return;

  // Before sync the two panes are fully independent; only track their state so the
  // first synced frame does not mistake old movement for a new leader.
  if (!state.synced) {
    remember();
    return;
  }

  const wsiMoved = changed(snapshot(state.wsi), state.last.wsi);
  const ssMoved = changed(snapshot(state.ss), state.last.ss);
  if (!wsiMoved && !ssMoved) return;

  let leader = wsiMoved ? "wsi" : "ss";
  if (wsiMoved && ssMoved) leader = state.lastInput || "wsi";

  if (leader === "wsi") followFromWsi();
  else followFromSs();

  remember();
  updateBox();
};

const scheduleTick = () => {
  if (!state.raf) state.raf = requestAnimationFrame(tick);
};

/* ---- red field-of-view box ------------------------------------------------------- */

const ensureBox = (ss) => {
  if (ss.box) return;
  const box = document.createElement("div");
  box.className = "block-image-fov-box";
  const label = document.createElement("div");
  label.className = "block-image-fov-label";
  box.appendChild(label);
  ss.viewer.element.appendChild(box);
  ss.box = box;
  ss.label = label;
};

const hideBox = () => {
  if (state.ss && state.ss.box) state.ss.box.style.display = "none";
};

// Physical scale bar on the block image, styled like the slide viewer's
// (viewer-scalebar.js): a fixed-width bracket labelled with the distance it spans at
// the block image's current zoom. Always the block image's own true scale, so it
// keeps reading correctly when the block image is held at its zoom limit.
const SCALE_BAR_PX = 110;

const formatLength = (um) => {
  if (um >= 1000) return `${(um / 1000).toFixed(2)} mm`;
  return `${um >= 100 ? Math.round(um) : um.toFixed(1)} µm`;
};

const updateScaleBar = () => {
  const { ss } = state;
  if (!ready(ss)) return;
  if (!ss.scaleBar) {
    const bar = document.createElement("div");
    bar.className = "block-image-scale-bar";
    const ruler = document.createElement("div");
    ruler.className = "block-image-scale-bar-ruler";
    ruler.style.width = `${SCALE_BAR_PX}px`;
    bar.appendChild(ruler);
    ss.viewer.element.appendChild(bar);
    ss.scaleBar = bar;
    ss.scaleBarLabel = ruler;
  }
  ss.scaleBarLabel.textContent = formatLength((SCALE_BAR_PX * ss.trueMpp) / imageZoom(ss));
};

const updateBox = () => {
  const { wsi, ss } = state;
  updateScaleBar();
  if (!state.synced || !ready(wsi) || !ready(ss)) {
    hideBox();
    return;
  }
  const max = maxImageZoom(ss);
  if (!(state.wantedIz > max * (1 + AT_LIMIT))) {
    hideBox();
    return;
  }
  ensureBox(ss);
  const wsiPane = wsi.viewer.viewport.getContainerSize();
  const ssPane = ss.viewer.viewport.getContainerSize();
  // Sized with the true (measured) scale: the zoom the block image is actually at,
  // against the zoom that would show the WSI field at true physical size.
  const { width, height } = fovBoxSize({
    paneW: wsiPane.x,
    paneH: wsiPane.y,
    izMax: imageZoom(ss),
    izWanted: matchImageZoom(imageZoom(wsi), wsi.mpp, ss.trueMpp),
  });
  const fov = fovMicrons(wsiPane.x, wsiPane.y, wsi.mpp, imageZoom(wsi));
  Object.assign(ss.box.style, {
    display: "block",
    width: `${width}px`,
    height: `${height}px`,
    left: `${(ssPane.x - width) / 2}px`,
    top: `${(ssPane.y - height) / 2}px`,
  });
  ss.label.textContent = formatFov(fov) + (wsi.assumed ? " (scale assumed)" : "");
};

/* ---- zooming the block image past its limit -------------------------------------- */

// Returns true when it handled the zoom, false when the default zoom should run.
const zoomSsVirtually = (factor) => {
  const { wsi, ss } = state;
  if (!state.synced || !ready(wsi) || !ready(ss)) return false;
  const max = maxImageZoom(ss);
  const iz = imageZoom(ss);
  const wanted = state.wantedIz === null ? iz : state.wantedIz;
  const atLimit = iz >= max * (1 - AT_LIMIT);
  if (!atLimit || (factor <= 1 && wanted <= max * (1 + AT_LIMIT))) return false;

  state.lastInput = "ss";
  const next = wanted * factor;
  state.wantedIz = next;
  if (next <= max) {
    // Back under the limit: zoom the block image itself; the tick follows.
    setImageZoom(ss, next, false);
  } else {
    // Still past the limit: zoom the WSI; the tick sees the WSI move and follows,
    // which keeps the block image at its limit and resizes the box.
    setImageZoom(wsi, matchImageZoom(next, ss.mpp, wsi.mpp), false);
  }
  return true;
};

/* ---- WSI constraints while synced ------------------------------------------------ */

// The WSI is configured with visibilityRatio 1 + constrainDuringPan, which would make
// it clamp at the image edge and fight the block image when following it.
const relaxWsiConstraints = () => {
  const { wsi } = state;
  if (!ready(wsi) || state.savedWsiConstraints) return;
  state.savedWsiConstraints = {
    visibilityRatio: wsi.viewer.viewport.visibilityRatio,
    constrainDuringPan: wsi.viewer.constrainDuringPan,
  };
  wsi.viewer.viewport.visibilityRatio = 0;
  wsi.viewer.constrainDuringPan = false;
};

const restoreWsiConstraints = () => {
  const { wsi, savedWsiConstraints } = state;
  if (wsi && savedWsiConstraints) {
    wsi.viewer.viewport.visibilityRatio = savedWsiConstraints.visibilityRatio;
    wsi.viewer.constrainDuringPan = savedWsiConstraints.constrainDuringPan;
  }
  state.savedWsiConstraints = null;
};

/* ---- landmarks ------------------------------------------------------------------- */

const clearOverlays = () => {
  state.overlays.forEach(({ viewer, element }) => {
    try {
      viewer.removeOverlay(element);
    } catch (e) {
      // viewer already destroyed
    }
  });
  state.overlays = [];
};

/* ---- registration ---------------------------------------------------------------- */

// Handlers shared by both panes. Returns a cleanup function.
const attach = (paneName, viewer) => {
  const onChange = () => scheduleTick();
  const onKey = (event) => {
    const keyCode = event.originalEvent && event.originalEvent.keyCode;
    if (state.synced && ROTATE_FLIP_KEYS.has(keyCode)) {
      event.preventDefaultAction = true;
    }
  };
  const onClick = (event) => {
    if (!state.pointHandler || !event.quick) return;
    const pane = state[paneName];
    if (!ready(pane)) return;
    event.preventDefaultAction = true; // no click-to-zoom while placing points
    const viewportPoint = viewer.viewport.pointFromPixel(event.position, true);
    const p = tiled(pane).viewportToImageCoordinates(viewportPoint);
    state.pointHandler(paneName, { x: p.x, y: p.y });
  };
  const onInput = () => {
    state.lastInput = paneName;
  };

  viewer.addHandler("viewport-change", onChange);
  viewer.addHandler("open", onChange);
  viewer.addHandler("canvas-key", onKey);
  viewer.addHandler("canvas-click", onClick);
  viewer.element.addEventListener("pointerdown", onInput, true);
  viewer.element.addEventListener("wheel", onInput, { capture: true, passive: true });

  return () => {
    viewer.removeHandler("viewport-change", onChange);
    viewer.removeHandler("open", onChange);
    viewer.removeHandler("canvas-key", onKey);
    viewer.removeHandler("canvas-click", onClick);
    viewer.element.removeEventListener("pointerdown", onInput, true);
    viewer.element.removeEventListener("wheel", onInput, { capture: true });
  };
};

// Prototype diagnostics: the numbers the scale match is built from. Also available
// on demand as window.__viewerSync.debugInfo() in the browser console.
const debugInfo = () => {
  const { wsi, ss } = state;
  if (!ready(wsi) || !ready(ss)) return { paired: false };
  const wsiSize = tiled(wsi).getContentSize();
  const ssSize = tiled(ss).getContentSize();
  return {
    paired: true,
    synced: state.synced,
    wsiMpp: wsi.mpp,
    wsiMppAssumed: wsi.assumed,
    wsiImagePx: `${wsiSize.x} × ${wsiSize.y}`,
    wsiImageMm: `${((wsiSize.x * wsi.mpp) / 1000).toFixed(1)} × ${((wsiSize.y * wsi.mpp) / 1000).toFixed(1)}`,
    ssMpp: ss.mpp,
    ssTrueMpp: ss.trueMpp,
    ssNominalMpp: ss.nominalMpp,
    ssImagePx: `${ssSize.x} × ${ssSize.y}`,
    ssImageMm: `${((ssSize.x * ss.trueMpp) / 1000).toFixed(1)} × ${((ssSize.y * ss.trueMpp) / 1000).toFixed(1)}`,
    wsiImageZoom: imageZoom(wsi),
    ssImageZoom: imageZoom(ss),
    ssImageZoomWanted: state.wantedIz,
    ssImageZoomMax: maxImageZoom(ss),
    wsiUmPerScreenPx: wsi.mpp / imageZoom(wsi),
    ssUmPerScreenPx: ss.mpp / imageZoom(ss),
  };
};

// Fresh pairing: start tracking. The panes stay independent until sync starts.
const startPairing = () => {
  state.last = { wsi: null, ss: null };
  state.wantedIz = null;
  if (ready(state.wsi) && ready(state.ss)) {
    remember();
    updateBox();
    // eslint-disable-next-line no-console
    console.info("[viewer-sync] paired", debugInfo());
  }
};

// While a transform exists it sets the block image's two scales:
//   trueMpp - from T.k, what positions are mapped with; also sizes the red box and
//             the scale bar, so they are physically right.
//   mpp     - from T.kZoom, what the displayed zoom is matched with. Equal to the
//             nominal µm/px unless the points showed it is completely different
//             (viewer-sync-math.js fitLandmarksWithScaleCheck).
const applyTransformScale = (T) => {
  if (state.wsi && state.ss && T && T.k > 0) {
    state.ss.trueMpp = state.wsi.mpp / T.k;
    state.ss.mpp = state.wsi.mpp / (T.kZoom > 0 ? T.kZoom : T.k);
  }
};

const dropSync = () => {
  state.synced = false;
  state.T = null;
  restoreWsiConstraints();
  hideBox();
};

const viewerSync = {
  /** Main slide viewer (index 0 only, single-slide mode only). */
  registerWsi(viewer, { mpp, assumed }) {
    if (state.wsi && state.wsi.viewer === viewer) {
      state.wsi.mpp = mpp;
      state.wsi.assumed = assumed;
      return;
    }
    this.unregisterWsi();
    state.wsi = { viewer, mpp, assumed, cleanup: attach("wsi", viewer) };
    startPairing();
  },

  unregisterWsi(viewer) {
    if (!state.wsi || (viewer && state.wsi.viewer !== viewer)) return;
    dropSync();
    clearOverlays();
    state.wsi.cleanup();
    state.wsi = null;
  },

  /**
   * Lets the controller read the current Redux sync state and report a sync it had
   * to drop. Set by viewer-container.js.
   */
  setSyncHandlers({ getSync, onSyncLost } = {}) {
    state.getSync = getSync || null;
    state.onSyncLost = onSyncLost || null;
  },

  /**
   * SectionStar viewer; call once its image has opened. `key` is the image's state
   * (sectioning / tape / slide): White and ClaraSight of the same state share one
   * alignment, which is carried over when the user switches between them.
   */
  registerSs(viewer, mpp, key) {
    this.unregisterSs();
    const onScroll = (event) => {
      const factor = Math.pow(viewer.zoomPerScroll, event.scroll);
      if (zoomSsVirtually(factor)) event.preventDefaultAction = true;
    };
    viewer.addHandler("canvas-scroll", onScroll);
    // The scale bar follows the block image on its own, paired or not.
    const onViewportChange = () => updateScaleBar();
    viewer.addHandler("viewport-change", onViewportChange);
    const cleanup = attach("ss", viewer);
    state.ss = {
      viewer,
      mpp,
      // nominal µm/px; a landmark fit may set trueMpp (positions, red box, scale
      // bar) and mpp (displayed zoom) differently - see applyTransformScale
      nominalMpp: mpp,
      trueMpp: mpp,
      cleanup: () => {
        viewer.removeHandler("canvas-scroll", onScroll);
        viewer.removeHandler("viewport-change", onViewportChange);
        cleanup();
      },
      box: null,
      label: null,
      scaleBar: null,
      scaleBarLabel: null,
    };
    updateScaleBar();
    startPairing();
    this.carryOverAfterSwitch(key);
  },

  /**
   * After switching White <-> ClaraSight of the same state, Redux still holds the
   * sync (viewer-reducer.js syncAfterSelecting). Re-apply its points and, if synced,
   * the transform - but only if the new image has the same pixel size as the one
   * the alignment was made on. Otherwise its points and transform would land in the
   * wrong place, so the sync is dropped instead.
   */
  carryOverAfterSwitch(key) {
    const size = tiled(state.ss).getContentSize();
    const info = { key, width: size.x, height: size.y };
    const previous = state.lastSsInfo;
    state.lastSsInfo = info;

    const sync = state.getSync ? state.getSync() : null;
    if (!sync) return;
    const { landmarks } = sync;
    const hasAlignment =
      sync.synced || sync.transform || landmarks.wsi.length > 0 || landmarks.ss.length > 0;
    if (!hasAlignment) return;

    const sameGrid =
      previous &&
      previous.key === key &&
      previous.width === info.width &&
      previous.height === info.height;
    if (!sameGrid) {
      // eslint-disable-next-line no-console
      console.warn("[viewer-sync] image size differs from the aligned one; sync dropped", {
        previous,
        current: info,
      });
      if (state.onSyncLost) state.onSyncLost();
      return;
    }

    this.setLandmarks(landmarks);
    if (sync.synced && sync.transform) {
      this.setSynced(true, sync.transform);
    }
  },

  unregisterSs(viewer) {
    if (!state.ss || (viewer && state.ss.viewer !== viewer)) return;
    dropSync();
    clearOverlays();
    if (state.ss.box) state.ss.box.remove();
    if (state.ss.scaleBar) state.ss.scaleBar.remove();
    state.ss.cleanup();
    state.ss = null;
    if (state.raf) {
      cancelAnimationFrame(state.raf);
      state.raf = 0;
    }
  },

  isPaired() {
    return ready(state.wsi) && ready(state.ss);
  },

  debugInfo,

  /**
   * Prototype calibration: override the open block image's µm/px from the console,
   * e.g. __viewerSync.setSsMpp(8), and re-match scale. Lasts until the image changes.
   */
  setSsMpp(mpp) {
    if (!ready(state.ss) || !(mpp > 0)) return debugInfo();
    state.ss.mpp = mpp;
    state.ss.trueMpp = mpp;
    state.ss.nominalMpp = mpp;
    state.wantedIz = null;
    followFromWsi();
    remember();
    updateBox();
    return debugInfo();
  },

  /** Zoom buttons on the block image: same limit handling as the mouse wheel. */
  zoomSsBy(viewer, factor) {
    if (state.ss && state.ss.viewer === viewer && zoomSsVirtually(factor)) return;
    viewer.viewport.zoomBy(factor);
    viewer.viewport.applyConstraints();
  },


  /**
   * Landmark fit (3+ pairs): rotation, translation and scale from the points for
   * positions; displayed zoom stays at the nominal µm/px unless the points say it
   * is completely different.
   */
  fitFromLandmarks(landmarks) {
    const { wsi, ss } = state;
    if (!ready(wsi) || !ready(ss)) return null;
    const kNominal = wsi.mpp / ss.nominalMpp;
    const fit = fitLandmarksWithScaleCheck(landmarks.wsi, landmarks.ss, kNominal);
    if (!fit) return null;
    const positionMpp = wsi.mpp / fit.T.k;
    return {
      transform: fit.T,
      residualUm: fit.residualPx === null ? null : fit.residualPx * positionMpp,
      scaleCheck: {
        ...fit.scaleCheck,
        nominalMpp: ss.nominalMpp,
        // µm/px of the block image implied by the points (null if not measurable);
        // positions are mapped with this whenever it exists
        measuredMpp: fit.scaleCheck.ratio ? ss.nominalMpp / fit.scaleCheck.ratio : null,
        // µm/px the displayed zoom is matched with
        zoomMpp: wsi.mpp / fit.T.kZoom,
      },
    };
  },

  /** The transform was dropped: go back to the image's nominal µm/px. */
  clearTransform() {
    const { ss } = state;
    if (!ready(ss) || state.synced) return;
    ss.mpp = ss.nominalMpp;
    ss.trueMpp = ss.nominalMpp;
    updateScaleBar();
  },

  /**
   * Sync on: rotate the block image to match the slide, then match its zoom and
   * move it to the tissue at the slide's centre, all instantly. From then on the
   * panes follow each other through T.
   */
  setSynced(on, T) {
    if (!on) {
      dropSync();
      return;
    }
    const { wsi, ss } = state;
    if (!T || !ready(wsi) || !ready(ss)) return;
    state.synced = true;
    state.T = T;
    applyTransformScale(T);
    ss.viewer.viewport.setRotation(
      followerRotation(wsi.viewer.viewport.getRotation(), T.theta),
      true,
    );
    relaxWsiConstraints();
    state.wantedIz = null;
    followFromWsi();
    remember();
    updateBox();
  },

  /** pointHandler(pane, imagePoint) while placing landmarks; null to stop. */
  setPointHandler(handler) {
    state.pointHandler = handler || null;
  },

  /**
   * Markers are only shown while placing points or while hovering the align button;
   * the points stay in the store either way.
   */
  setLandmarksVisible(visible) {
    state.landmarksVisible = !!visible;
    state.overlays.forEach(({ element }) => {
      element.style.visibility = state.landmarksVisible ? "visible" : "hidden";
    });
  },

  setLandmarks(landmarks) {
    clearOverlays();
    if (!landmarks) return;
    [
      ["wsi", landmarks.wsi],
      ["ss", landmarks.ss],
    ].forEach(([paneName, points]) => {
      const pane = state[paneName];
      if (!ready(pane)) return;
      points.forEach((p, i) => {
        const element = document.createElement("div");
        element.className = "sync-landmark";
        element.textContent = String(i + 1);
        element.style.visibility = state.landmarksVisible ? "visible" : "hidden";
        pane.viewer.addOverlay({
          element,
          location: tiled(pane).imageToViewportCoordinates(p.x, p.y),
          placement: OpenSeadragon.Placement.CENTER,
          // Stays at its point in the image but is never rotated with the view, so
          // the number always reads right side up.
          rotationMode: OpenSeadragon.OverlayRotationMode.NO_ROTATION,
          checkResize: false,
        });
        state.overlays.push({ viewer: pane.viewer, element });
      });
    });
  },
};

// Prototype only: lets testers run __viewerSync.debugInfo() in the browser console.
if (typeof window !== "undefined") {
  window.__viewerSync = viewerSync;
}

export default viewerSync;
